// #664 (F40) — the one modal that pings a user. The Officials tab (a manager
// or an admin), the bout page (the jury) and the Admin page (an admin) all open
// it. It owns the text, the lock of the write (#605), the refusal and the
// "sent" toast. The caller gives the context: the title, the link and the ids.
import { useState } from 'react';
import { pushPing } from '../../api/client';
import { useBusy } from '../../hooks/useBusy';
import { useToast } from '../../hooks/useToast';
import { useI18n } from '../../i18n';
import ConfirmModal from './ConfirmModal';

interface PingModalProps {
  /** The user who gets the ping. */
  target: { id: string; first_name: string };
  /** The title of the push and of the bell. It names the role of the sender,
   *  in the language of the sender (#557): pass a text of `t.roles`, never a
   *  literal. #603 — the title has words only: a phone draws no SVG. */
  title: string;
  /** The text in the box when the modal opens. */
  defaultText: string;
  /** The path that the notice opens: the tournament or the bout. */
  url?: string;
  /** A ping from a bout: the route checks the jury of this bout. */
  boutId?: string;
  /** A ping from a tournament: the route checks the manager of this tournament. */
  tournamentId?: string;
  /** The data-testid of Send. Default `ping-send`. */
  sendTestId?: string;
  onClose: () => void;
}

export default function PingModal({
  target, title, defaultText, url, boutId, tournamentId, sendTestId = 'ping-send', onClose,
}: PingModalProps) {
  const { t } = useI18n();
  const { showError, showSuccess } = useToast();
  // #605 — one ping at a time: a second tap sends no second notice.
  const sending = useBusy();
  const [text, setText] = useState(defaultText);

  function send() {
    return sending.run(async () => {
      try {
        await pushPing({ user_id: target.id, title, body: text, url, bout_id: boutId, tournament_id: tournamentId });
      } catch (err) {
        // The modal stays open: the user keeps the text and can send it again.
        showError(err);
        return;
      }
      onClose();
      showSuccess(t.tournament.notificationSent.replace('{name}', target.first_name));
    });
  }

  return (
    <ConfirmModal
      title={t.tournament.pingTitle} confirmLabel={t.tournament.pingSend}
      busy={sending.busy} testId={sendTestId}
      input={{ label: t.common.messageTo.replace('{name}', target.first_name), value: text, onChange: setText }}
      onConfirm={send} onCancel={onClose}
    />
  );
}
